import type { ReactNode } from 'react'
import { designTokens } from '@/utils/design-tokens'
import type { StrategySummaryMetrics } from '@/api/generated/models/StrategySummaryMetrics'
import RateBar from './RateBar'
import { getRate, strategyColors, type MetricKey } from '../strategyMetrics'

export type StatusColumn = 'taskCount' | 'itemCount' | MetricKey

export interface StatusRow {
  id: string
  name: string
  metrics: StrategySummaryMetrics
}

interface StatusTableProps {
  title: string
  hint: string
  firstColumnLabel: string
  columns: StatusColumn[]
  rows: StatusRow[]
  footnote: string
  emptyText?: string
}

const COLUMN_LABEL: Record<StatusColumn, string> = {
  taskCount: '세부실행과제',
  itemCount: '수행업무',
  task: '과제 진행률',
  item: '업무 진행률',
  budget: '예산 집행률',
  performance: '성과 달성률',
}

const columnWidth = (column: StatusColumn, rateCount: number): string => {
  if (column === 'taskCount') return '100px'
  if (column === 'itemCount') return '80px'
  return rateCount === 1 ? 'minmax(240px, 2fr)' : 'minmax(170px, 1.2fr)'
}

const renderCell = (
  column: StatusColumn,
  metrics: StrategySummaryMetrics
): ReactNode => {
  if (column === 'taskCount')
    return `${(metrics.task_count ?? 0).toLocaleString('ko-KR')}개`
  if (column === 'itemCount')
    return `${(metrics.task_item.total_count ?? 0).toLocaleString('ko-KR')}개`
  return <RateBar rate={getRate(metrics, column)} />
}

/** 실행과제별·부서별 현황 표 (가로 막대 + %) */
export default function StatusTable({
  title,
  hint,
  firstColumnLabel,
  columns,
  rows,
  footnote,
  emptyText = '표시할 데이터가 없습니다.',
}: StatusTableProps) {
  const font = designTokens.typography.fontFamily.sans[0]
  const rateCount = columns.filter(
    (c) => c !== 'taskCount' && c !== 'itemCount'
  ).length
  const gridTemplateColumns = [
    'minmax(160px, 1.2fr)',
    ...columns.map((c) => columnWidth(c, rateCount)),
  ].join(' ')
  // 세부실행과제 표시 여부에 따라 열 수가 바뀌므로 열 구성으로 최소 폭을 계산한다
  const minWidth =
    rateCount === 1
      ? 620
      : 220 +
        (columns.includes('taskCount') ? 116 : 0) +
        (columns.includes('itemCount') ? 96 : 0) +
        rateCount * 186
  const isCountColumn = (column: StatusColumn) =>
    column === 'taskCount' || column === 'itemCount'

  return (
    <section
      aria-label={title}
      style={{
        background: strategyColors.surface,
        border: `1px solid ${strategyColors.border}`,
        borderRadius: 12,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        fontFamily: font,
        color: strategyColors.text,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700 }}>{title}</h2>
        <span style={{ fontSize: 13, color: strategyColors.subText }}>
          {hint}
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth, display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns,
              gap: 16,
              padding: '10px 8px',
              fontSize: 13,
              fontWeight: 500,
              color: strategyColors.subText,
              borderBottom: `1px solid ${strategyColors.border}`,
            }}
          >
            <span>{firstColumnLabel}</span>
            {columns.map((column) => (
              <span
                key={column}
                style={{ textAlign: isCountColumn(column) ? 'right' : 'left' }}
              >
                {COLUMN_LABEL[column]}
              </span>
            ))}
          </div>
          {rows.length === 0 ? (
            <div
              style={{
                padding: '24px 8px',
                fontSize: 14,
                color: strategyColors.subText,
                textAlign: 'center',
              }}
            >
              {emptyText}
            </div>
          ) : (
            rows.map((row) => (
              <div
                key={row.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns,
                  gap: 16,
                  padding: '14px 8px',
                  alignItems: 'center',
                  borderBottom: `1px solid ${strategyColors.track}`,
                }}
              >
                <span style={{ fontWeight: 700 }}>{row.name}</span>
                {columns.map((column) => (
                  <div
                    key={column}
                    style={
                      isCountColumn(column)
                        ? { fontSize: 14, textAlign: 'right' }
                        : undefined
                    }
                  >
                    {renderCell(column, row.metrics)}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 12, color: strategyColors.subText }}>
        {footnote}
      </p>
    </section>
  )
}
